import React, { useState } from 'react';
import { HiPlus } from 'react-icons/hi2';

/**
 * The FAQ list at the foot of the certificate detail page.
 *
 * One item open at a time, matching the design, where the open item is the
 * only one with a filled ground. Built on <button> + aria-expanded rather than
 * <details> so the open panel can animate its height.
 *
 * Measured off the design at its 1920 width:
 *   row      70px in both states, 20px in from each edge, 12px between cards
 *   closed   white on a 1px #e2e8f0 edge, a 28px #f1f5f9 disc, a #5b667a plus
 *   open     #f5f7fa on a 1px #cbd5e1 edge, a 40px #0f1420 disc, a white plus
 *   answer   14px #5b667a, and 48px of air under it
 *
 * The mark stays a plus when the item is open — the design never draws a
 * minus, and the open card is already told apart by its ground, its edge and
 * its filled disc. The two discs are different sizes but share a centre,
 * because each sits in the same 40px slot.
 *
 * @param {{id: string, question: React.ReactNode, answer: React.ReactNode}[]} items
 * @param {string} defaultOpenId  Pass null to start with everything closed.
 */
const Accordion = ({ items, defaultOpenId, className = '' }) => {
    const [openId, setOpenId] = useState(
        defaultOpenId === undefined ? items[0]?.id : defaultOpenId
    );

    return (
        <div className={`tw-flex tw-flex-col tw-gap-3 ${className}`}>
            {items.map(item => {
                const isOpen = item.id === openId;

                return (
                    <div
                        key={item.id}
                        className={
                            'tw-rounded-xl tw-border tw-transition-colors tw-duration-200 ' +
                            (isOpen
                                ? 'tw-border-v2-line-mist tw-bg-v2-ground-200'
                                : 'tw-border-v2-ground-400 tw-bg-white')
                        }
                    >
                        <button
                            type="button"
                            id={`question-${item.id}`}
                            aria-expanded={isOpen}
                            aria-controls={`answer-${item.id}`}
                            onClick={() => setOpenId(isOpen ? null : item.id)}
                            className={
                                'tw-flex tw-h-[70px] tw-w-full tw-items-center tw-justify-between tw-gap-4 ' +
                                'tw-rounded-xl tw-px-5 tw-text-start tw-text-v2-label tw-font-bold tw-text-v2-text-ink ' +
                                'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand'
                            }
                        >
                            <span>{item.question}</span>

                            {/* A fixed 40px slot holds both discs, so the mark
                                keeps its centre as the card opens rather than
                                shifting when the smaller disc grows. */}
                            <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center">
                                <span
                                    className={
                                        'tw-flex tw-items-center tw-justify-center tw-rounded-full tw-transition-all tw-duration-200 ' +
                                        (isOpen
                                            ? 'tw-h-10 tw-w-10 tw-bg-v2-text-ink tw-text-white'
                                            : 'tw-h-7 tw-w-7 tw-bg-v2-ground-150 tw-text-v2-text-slate')
                                    }
                                >
                                    <HiPlus aria-hidden className={isOpen ? 'tw-h-[22px] tw-w-[22px]' : 'tw-h-4 tw-w-4'} />
                                </span>
                            </span>
                        </button>

                        {/* Kept mounted and collapsed by grid-rows so the panel can
                            animate to its natural height — a max-height guess would
                            either clip long answers or ease at the wrong speed.
                            `hidden` on the inner element keeps closed answers out of
                            the accessibility tree and out of Ctrl+F. */}
                        <div
                            id={`answer-${item.id}`}
                            role="region"
                            aria-labelledby={`question-${item.id}`}
                            className={
                                'tw-grid tw-transition-all tw-duration-300 motion-reduce:tw-transition-none ' +
                                (isOpen ? 'tw-grid-rows-[1fr]' : 'tw-grid-rows-[0fr]')
                            }
                        >
                            <div className="tw-overflow-hidden">
                                <div
                                    hidden={!isOpen}
                                    className="tw-px-5 tw-pb-12 tw-text-v2-sm tw-leading-[1.6] tw-text-v2-text-slate"
                                >
                                    {item.answer}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Accordion;
